import TrashIcon from "../../../shared/icons/TrashIcon";
import axios, { AxiosError } from "axios";
import React, { ComponentProps, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { deltamathAPI } from "../../../manager/utils";
import { useDeltaToastContext } from "../../../shared/contexts/ToasterContext";
import DeltaMathConfirmation from "../../../shared/DeltaMathConfirmation";
import DeltaMathTable from "../../../shared/DeltaMathTable";
import { DemoMode } from "../../../utils/demo";
import { REACT_APP_ADMIN_LINK } from "../../../utils";
import { IconButton } from "../../../student/components/generic/IconButton";
import Modal from "../../../student/components/generic/Modal";
import { Infotip } from "../../../shared/Infotip";

export default function CurrentAdmins({
  admins,
  school_id,
  partOfDistrict,
  demoMode,
}: {
  admins: any;
  school_id: any;
  partOfDistrict: boolean;
  demoMode: boolean;
}) {
  const toastContext = useDeltaToastContext();
  const queryClient = useQueryClient();
  const [cannotDowngrade, setCannotDowngrade] = useState("");
  const [showDeleteAdminConfirmation, setShowDeleteAdminConfirmation] =
    useState(false);
  const [userToDelete, setUserToDelete] = useState({
    id: "",
    first: "",
    last: "",
  });

  const removeAdmin = useMutation({
    mutationFn: () => {
      return axios.delete(
        deltamathAPI() +
          `/admin_new/manage/remove/${userToDelete.id}/${school_id}`
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-data"] });
      toastContext.addToast({
        status: "Success",
        message: "Admin Removed Succesfully",
      });
    },
    onError: (err: AxiosError<{ message: string }>) => {
      toastContext.addToast({
        status: "Error",
        message:
          err?.response?.data?.message || "There was an error with the request",
      });
    },
  });

  useEffect(() => {
    setShowDeleteAdminConfirmation(false);
  }, [removeAdmin.isSuccess]);

  const columns = React.useMemo(() => {
    const columnsDefinition: ComponentProps<typeof DeltaMathTable>["columns"] =
      [
        {
          Header: "First Name",
          accessor: "first",
          align: "left",
          Cell: (props: any) => {
            return <DemoMode value={props.value} type="person_first" />;
          },
        },
        {
          Header: "Last Name",
          accessor: "last",
          align: "left",
          Cell: (props: any) => {
            return <DemoMode value={props.value} type="person_last" />;
          },
        },
        {
          Header: "Email",
          accessor: "email",
          align: "left",
          Cell: (props: any) => {
            return <DemoMode value={props.value} type="person_email" />;
          },
        },
      ];

    columnsDefinition.push({
      Header: "",
      accessor: "showDeleteButton",
      disableSortBy: true,
      Cell: (props: { row: { original: any } }) => {
        const canDelete = !!props.row.original.showDeleteButton;
        const tooltipMsg =
          "You are unable to remove this admin from the site. Please reach out to support@deltamath.com for assistance.";

        return (
          <div className="flex justify-end">
            <Infotip
              message={!canDelete ? tooltipMsg : undefined}
              options={{
                tooltipLeft: true,
                alwaysWrap: true,
              }}
              ariaLabel="Delete unavailable"
            >
              <IconButton
                onClick={() => {
                  setShowDeleteAdminConfirmation(true);
                  setUserToDelete({
                    id: props.row.original._id,
                    first: props.row.original.first,
                    last: props.row.original.last,
                  });
                }}
                icon={<TrashIcon />}
                aria-label="Delete"
                disabled={!canDelete}
                className="delete-icon-btn transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </Infotip>
          </div>
        );
      },
    });
    return columnsDefinition;
  }, [admins, partOfDistrict, school_id, demoMode]);

  return (
    <>
      {admins && admins.length > 0 && (
        <div className="rounded-md border">
          <DeltaMathTable
            columns={columns}
            data={admins}
            options={{
              initialState: {
                sortBy: [
                  {
                    id: "last",
                    desc: false,
                  },
                ],
              },
            }}
          />
        </div>
      )}
      {admins && admins.length === 0 && (
        <>
          <p className="text-md my-1 mb-6 text-gray-500">No admins found.</p>

          <p>
            <Link
              className="underline hover:no-underline"
              to={`${REACT_APP_ADMIN_LINK}/schools/${school_id}/invite-admins`}
            >
              Invite an Admin
            </Link>{" "}
            to get started.
          </p>
        </>
      )}

      {showDeleteAdminConfirmation && userToDelete && (
        <DeltaMathConfirmation
          title={`Remove Admin`}
          message={
            <>
              Are you sure you want to remove{" "}
              <DemoMode value={userToDelete.first} type="person_first" />{" "}
              <DemoMode value={userToDelete.last} type="person_last" />
              &rsquo;s account? This admin will be removed from the school and
              will no longer have access to the school&apos;s data.
            </>
          }
          confirm="Delete"
          confirmAction={removeAdmin.mutate}
          cancel="Cancel"
          cancelAction={() => {
            setShowDeleteAdminConfirmation(false);
            setUserToDelete({
              id: "",
              first: "",
              last: "",
            });
          }}
        />
      )}
      {cannotDowngrade && (
        <Modal
          visible={!!cannotDowngrade}
          onClose={() => setCannotDowngrade("")}
          header={"Cannot Downgrade Admin"}
          body={cannotDowngrade}
          confirmButtonText="OK"
        />
      )}
    </>
  );
}
